import type { Dict } from "../types";

// Journal d'activité. Les phrases `ActivityLog.sentence.<entité>.<action>` couvrent
// exactement les paires que les déclencheurs SQL peuvent écrire (liste typée dans
// src/lib/activityLog/knownActions.ts, gardée par describe.test.ts).
export const activityLog: Dict = {
  en: {
    "ActivityLog.title": "Activity log",
    "ActivityLog.sensitiveBadge": "Worth a look",
    "ActivityLog.category.appointment": "Appointments",
    "ActivityLog.category.money": "Money and cash drawer",
    "ActivityLog.category.customer": "Customer file",

    "ActivityLog.subtitle": "Who did what, and when, on appointments, money and customer files. Kept for 12 months.",
    "ActivityLog.filter.button": "Filters",
    "ActivityLog.filter.period": "Period",
    "ActivityLog.filter.period.today": "Today",
    "ActivityLog.filter.period.7d": "7 days",
    "ActivityLog.filter.period.30d": "30 days",
    "ActivityLog.filter.period.custom": "Custom",
    "ActivityLog.filter.from": "From",
    "ActivityLog.filter.to": "To",
    "ActivityLog.filter.person": "Person",
    "ActivityLog.filter.person.all": "Everyone",
    "ActivityLog.filter.category": "Category",
    "ActivityLog.filter.category.all": "All",
    "ActivityLog.filter.searchPlaceholder": "Search a customer, an invoice…",
    "ActivityLog.count.one": "{count} action shown",
    "ActivityLog.count.many": "{count} actions shown",
    "ActivityLog.empty": "No activity for these filters.",
    "ActivityLog.emptyPlain": "No action recorded yet.",
    "ActivityLog.error": "The activity log could not be loaded.",
    "ActivityLog.errorMore": "The rest of the log could not be loaded.",
    "ActivityLog.retry": "Try again",
    "ActivityLog.loadMore": "Load more",
    "ActivityLog.loadingMore": "Loading…",
    "ActivityLog.detail.when": "When",
    "ActivityLog.detail.by": "By",
    "ActivityLog.detail.category": "Category",
    "ActivityLog.detail.categorySensitive": "{category}, worth a look",
    "ActivityLog.detail.changes": "What changed",
    "ActivityLog.detail.openCustomer": "Open the customer file",
    "ActivityLog.detail.by.owner": "{name} (owner)",
    "ActivityLog.detail.by.staff": "{name} (staff member)",
    "ActivityLog.detail.by.customer": "{name} (customer)",
    "ActivityLog.detail.by.customerOnline": "The customer, from online booking",

    "ActivityLog.actor.customer": "Customer (online)",
    "ActivityLog.actor.system": "System",
    "ActivityLog.actor.support": "Flowcut support",
    "ActivityLog.actor.unknown": "Unknown person",
    "ActivityLog.actor.label.voiceAssistant": "Voice assistant",
    "ActivityLog.actor.label.autoApproval": "Automatic approval",
    "ActivityLog.actor.label.autoRefund": "Automatic refund",
    "ActivityLog.actor.label.confirmationLink": "Customer (confirmation link)",
    "ActivityLog.label.unknown": "unknown customer",
    "ActivityLog.amount.unknown": "an amount",

    "ActivityLog.importKind.appointments": "appointments",
    "ActivityLog.importKind.customers": "customers",
    "ActivityLog.importKind.gift_cards": "gift cards",

    "ActivityLog.sentence.fallback": "changed an item",
    "ActivityLog.sentence.appointment.fallback": "edited the appointment for {label}",
    "ActivityLog.sentence.blocked_slot.fallback": "edited blocked time ({label})",
    "ActivityLog.sentence.payment.fallback": "edited a payment on invoice {label}",
    "ActivityLog.sentence.invoice.fallback": "edited invoice {label}",
    "ActivityLog.sentence.credit_note.fallback": "edited credit note {label}",
    "ActivityLog.sentence.cash_movement.fallback": "edited the cash drawer",
    "ActivityLog.sentence.gift_card.fallback": "edited gift card {label}",
    "ActivityLog.sentence.balance.fallback": "edited {label}'s balance",
    "ActivityLog.sentence.customer.fallback": "edited {label}'s profile",
    "ActivityLog.sentence.technical_sheet.fallback": "edited a technical note for {label}",
    "ActivityLog.sentence.import.fallback": "ran an import",

    "ActivityLog.sentence.appointment.created": "created the appointment for {label}",
    "ActivityLog.sentence.appointment.moved": "moved the appointment for {label}",
    "ActivityLog.sentence.appointment.reassigned": "changed the employee on the appointment for {label}",
    "ActivityLog.sentence.appointment.cancelled": "cancelled the appointment for {label}",
    "ActivityLog.sentence.appointment.restored": "restored the appointment for {label}",
    "ActivityLog.sentence.appointment.no_show": "marked a no-show on the appointment for {label}",
    "ActivityLog.sentence.appointment.no_show_removed": "removed the no-show from the appointment for {label}",
    "ActivityLog.sentence.appointment.confirmed": "confirmed the appointment for {label}",
    "ActivityLog.sentence.appointment.services_changed": "changed the services of the appointment for {label}",
    "ActivityLog.sentence.appointment.updated": "edited the appointment for {label}",
    "ActivityLog.sentence.appointment.deleted": "deleted the appointment for {label}",
    "ActivityLog.sentence.blocked_slot.created": "blocked time ({label})",
    "ActivityLog.sentence.blocked_slot.updated": "edited blocked time ({label})",
    "ActivityLog.sentence.blocked_slot.deleted": "deleted blocked time ({label})",

    "ActivityLog.sentence.payment.payment_received": "collected {amount} on invoice {label}",
    "ActivityLog.sentence.payment.tip_received": "collected a {amount} tip on invoice {label}",
    "ActivityLog.sentence.payment.refunded": "refunded {amount} on invoice {label}",
    "ActivityLog.sentence.payment.chargeback": "recorded a {amount} chargeback on invoice {label}",
    "ActivityLog.sentence.payment.updated": "edited a payment on invoice {label}",
    "ActivityLog.sentence.payment.deleted": "deleted a {amount} payment from invoice {label}",
    "ActivityLog.sentence.invoice.created": "created invoice {label}",
    "ActivityLog.sentence.invoice.voided": "voided invoice {label}",
    "ActivityLog.sentence.invoice.discount_changed": "changed the discount on invoice {label}",
    "ActivityLog.sentence.invoice.updated": "edited invoice {label}",
    "ActivityLog.sentence.invoice.deleted": "deleted invoice {label}",
    "ActivityLog.sentence.credit_note.created": "issued credit note {label} for {amount}",
    "ActivityLog.sentence.credit_note.updated": "edited credit note {label}",
    "ActivityLog.sentence.credit_note.deleted": "deleted credit note {label}",
    "ActivityLog.sentence.cash_movement.drawer_opened": "opened the drawer with {amount}",
    "ActivityLog.sentence.cash_movement.drawer_closed": "closed the drawer at {amount}",
    "ActivityLog.sentence.cash_movement.manual_add": "added {amount} to the drawer",
    "ActivityLog.sentence.cash_movement.manual_withdraw": "took {amount} out of the drawer",
    "ActivityLog.sentence.cash_movement.vault_deposit": "deposited {amount} in the safe",
    "ActivityLog.sentence.cash_movement.tip_payout": "paid out {amount} in tips",
    "ActivityLog.sentence.cash_movement.deleted": "deleted a {amount} drawer movement",
    "ActivityLog.sentence.gift_card.created": "created gift card {label} for {amount}",
    "ActivityLog.sentence.gift_card.updated": "edited gift card {label}",
    "ActivityLog.sentence.gift_card.deleted": "deleted gift card {label}",
    "ActivityLog.sentence.gift_card.balance_adjusted": "adjusted gift card {label} balance by {amount}",
    "ActivityLog.sentence.balance.added": "added {amount} to {label}'s balance",
    "ActivityLog.sentence.balance.cancelled": "cancelled {amount} from {label}'s balance",

    "ActivityLog.sentence.customer.created": "created {label}'s profile",
    "ActivityLog.sentence.customer.updated": "edited {label}'s profile",
    "ActivityLog.sentence.customer.contact_changed": "changed {label}'s contact details",
    "ActivityLog.sentence.customer.blacklisted": "blacklisted {label}",
    "ActivityLog.sentence.customer.unblacklisted": "removed {label} from the blacklist",
    "ActivityLog.sentence.customer.merged": "merged {label}'s profile",
    "ActivityLog.sentence.customer.deleted": "deleted {label}'s profile",
    "ActivityLog.sentence.technical_sheet.created": "added a technical note to {label}'s profile",
    "ActivityLog.sentence.technical_sheet.updated": "edited a technical note for {label}",
    "ActivityLog.sentence.technical_sheet.deleted": "deleted a technical note for {label}",
    "ActivityLog.sentence.import.imported": "imported {count} {kind}",

    "ActivityLog.field.unknown": "Changed field",
    "ActivityLog.field.appointment_date": "Date",
    "ActivityLog.field.appointment_time": "Time",
    "ActivityLog.field.hairdresser_name": "Employee",
    "ActivityLog.field.hairdresser_id": "Employee",
    "ActivityLog.field.status": "Status",
    "ActivityLog.field.attendance_status": "Attendance",
    "ActivityLog.field.service_name": "Services",
    "ActivityLog.field.total_price": "Total price",
    "ActivityLog.field.total_duration": "Total duration (min)",
    "ActivityLog.field.notes": "Notes",
    "ActivityLog.field.staff_note": "Staff note",
    "ActivityLog.field.blocked_date": "Date",
    "ActivityLog.field.start_time": "Start",
    "ActivityLog.field.end_time": "End",
    "ActivityLog.field.reason": "Reason",
    "ActivityLog.field.is_full_day": "Full day",
    "ActivityLog.field.room_id": "Room",
    "ActivityLog.field.room_name": "Room",
    "ActivityLog.field.occurrences": "Blocks created",
    "ActivityLog.field.employees": "Employees",
    "ActivityLog.field.amount": "Amount",
    "ActivityLog.field.payment_method": "Payment method",
    "ActivityLog.field.payment_type": "Payment type",
    "ActivityLog.field.discount": "Discount",
    "ActivityLog.field.is_complimentary": "Complimentary",
    "ActivityLog.field.tax_exempt": "Tax exempt",
    "ActivityLog.field.settled_externally": "Settled outside Flowcut",
    "ActivityLog.field.deleted_at": "Deleted on",
    "ActivityLog.field.applied_to_invoice_id": "Applied to an invoice",
    "ActivityLog.field.initial_amount": "Initial amount",
    "ActivityLog.field.expiration_date": "Expiration date",
    "ActivityLog.field.primary_customer_id": "Cardholder",
    "ActivityLog.field.allergies": "Allergies",
    "ActivityLog.field.style_notes": "Style notes",
    "ActivityLog.field.internal_notes": "Internal notes",
    "ActivityLog.field.preferred_stylist_id": "Preferred Employee",
    "ActivityLog.field.preferred_stylist_name": "Preferred Employee",
    "ActivityLog.field.preferred_products": "Preferred products",
    "ActivityLog.field.client_type": "Customer type",
    "ActivityLog.field.is_blacklisted": "Blacklist",
    "ActivityLog.field.blacklist_reason": "Blacklist reason",
    "ActivityLog.field.birth_date": "Birth date",
    "ActivityLog.field.birth_day": "Birth day",
    "ActivityLog.field.birth_month": "Birth month",
    "ActivityLog.field.gender": "Gender",
    "ActivityLog.field.secondary_phone": "Secondary phone",
    "ActivityLog.field.emergency_contact_name": "Emergency contact",
    "ActivityLog.field.emergency_contact_phone": "Emergency contact phone",
    "ActivityLog.field.emergency_contact_relation": "Emergency contact relationship",
    "ActivityLog.field.acquisition_source": "How they found the salon",
    "ActivityLog.field.consent_collected": "Consent collected",
    "ActivityLog.field.full_name": "Name",
    "ActivityLog.field.email": "Email",
    "ActivityLog.field.phone": "Phone",
    "ActivityLog.field.merged_into_id": "Merged into another profile",
    "ActivityLog.field.note": "Note",
    "ActivityLog.field.entry_date": "Note date",
    "ActivityLog.field.employee_name": "Author of the note",
    "ActivityLog.field.count": "Number",
    "ActivityLog.field.kind": "Type",

    "ActivityLog.value.empty": "None",
    "ActivityLog.value.emptyFeminine": "None",
    "ActivityLog.value.set": "Set",
    "ActivityLog.value.setFeminine": "Set",
    "ActivityLog.value.yes": "Yes",
    "ActivityLog.value.no": "No",
    "ActivityLog.value.status.completed": "Completed",
    "ActivityLog.value.status.partially_paid": "Partially paid",
    "ActivityLog.value.status.approved": "Approved",
    "ActivityLog.value.status.applied": "Applied",
    "ActivityLog.value.status.expired": "Expired",
    "ActivityLog.value.payment_type.payment": "Payment",
    "ActivityLog.value.payment_type.tip": "Tip",
    "ActivityLog.value.payment_type.refund": "Refund",
    "ActivityLog.value.payment_type.chargeback": "Chargeback",
    "ActivityLog.value.masked": "Hidden",
    "ActivityLog.value.erased": "Erased",
    "ActivityLog.tab.customer": "Log",
    "ActivityLog.tab.employee": "Log",
    "ActivityLog.appointmentButton": "History",
    "ActivityLog.dialogTitle.appointment": "Appointment history",
    "ActivityLog.employeeNoAccount": "This employee has no linked account, so no action can be attributed to them.",
  },
  fr: {
    "ActivityLog.title": "Journal d'activité",
    "ActivityLog.sensitiveBadge": "À surveiller",
    "ActivityLog.category.appointment": "Rendez-vous",
    "ActivityLog.category.money": "Argent et caisse",
    "ActivityLog.category.customer": "Fiche client",

    "ActivityLog.subtitle": "Qui a fait quoi, et quand, sur les rendez-vous, l'argent et les fiches clients. Conservé 12 mois.",
    "ActivityLog.filter.button": "Filtres",
    "ActivityLog.filter.period": "Période",
    "ActivityLog.filter.period.today": "Aujourd'hui",
    "ActivityLog.filter.period.7d": "7 jours",
    "ActivityLog.filter.period.30d": "30 jours",
    "ActivityLog.filter.period.custom": "Personnalisée",
    "ActivityLog.filter.from": "Du",
    "ActivityLog.filter.to": "Au",
    "ActivityLog.filter.person": "Personne",
    "ActivityLog.filter.person.all": "Tout le monde",
    "ActivityLog.filter.category": "Catégorie",
    "ActivityLog.filter.category.all": "Tout",
    "ActivityLog.filter.searchPlaceholder": "Rechercher un client, une facture…",
    "ActivityLog.count.one": "{count} action affichée",
    "ActivityLog.count.many": "{count} actions affichées",
    "ActivityLog.empty": "Aucune action pour ces filtres.",
    "ActivityLog.emptyPlain": "Aucune action enregistrée pour le moment.",
    "ActivityLog.error": "Le journal n'a pas pu être chargé.",
    "ActivityLog.errorMore": "La suite du journal n'a pas pu être chargée.",
    "ActivityLog.retry": "Réessayer",
    "ActivityLog.loadMore": "Charger plus",
    "ActivityLog.loadingMore": "Chargement…",
    "ActivityLog.detail.when": "Quand",
    "ActivityLog.detail.by": "Par",
    "ActivityLog.detail.category": "Catégorie",
    "ActivityLog.detail.categorySensitive": "{category}, à surveiller",
    "ActivityLog.detail.changes": "Ce qui a changé",
    "ActivityLog.detail.openCustomer": "Ouvrir la fiche client",
    "ActivityLog.detail.by.owner": "{name} (propriétaire)",
    "ActivityLog.detail.by.staff": "{name} (membre du personnel)",
    "ActivityLog.detail.by.customer": "{name} (client)",
    "ActivityLog.detail.by.customerOnline": "Le client, depuis la réservation en ligne",

    "ActivityLog.actor.customer": "Client (en ligne)",
    "ActivityLog.actor.system": "Système",
    "ActivityLog.actor.support": "Support Flowcut",
    "ActivityLog.actor.unknown": "Personne inconnue",
    "ActivityLog.actor.label.voiceAssistant": "Assistant vocal",
    "ActivityLog.actor.label.autoApproval": "Approbation automatique",
    "ActivityLog.actor.label.autoRefund": "Remboursement automatique",
    "ActivityLog.actor.label.confirmationLink": "Client (lien de confirmation)",
    "ActivityLog.label.unknown": "client inconnu",
    "ActivityLog.amount.unknown": "un montant",

    "ActivityLog.importKind.appointments": "rendez-vous",
    "ActivityLog.importKind.customers": "clients",
    "ActivityLog.importKind.gift_cards": "cartes-cadeaux",

    "ActivityLog.sentence.fallback": "a modifié un élément",
    "ActivityLog.sentence.appointment.fallback": "a modifié le rendez-vous {deLabel}",
    "ActivityLog.sentence.blocked_slot.fallback": "a modifié une plage bloquée ({label})",
    "ActivityLog.sentence.payment.fallback": "a modifié un paiement de la facture {label}",
    "ActivityLog.sentence.invoice.fallback": "a modifié la facture {label}",
    "ActivityLog.sentence.credit_note.fallback": "a modifié la note de crédit {label}",
    "ActivityLog.sentence.cash_movement.fallback": "a modifié la caisse",
    "ActivityLog.sentence.gift_card.fallback": "a modifié la carte-cadeau {label}",
    "ActivityLog.sentence.balance.fallback": "a modifié le solde {deLabel}",
    "ActivityLog.sentence.customer.fallback": "a modifié la fiche {deLabel}",
    "ActivityLog.sentence.technical_sheet.fallback": "a modifié une note technique {deLabel}",
    "ActivityLog.sentence.import.fallback": "a lancé un import",

    "ActivityLog.sentence.appointment.created": "a créé le rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.moved": "a déplacé le rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.reassigned": "a changé l'employé du rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.cancelled": "a annulé le rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.restored": "a réactivé le rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.no_show": "a marqué une absence au rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.no_show_removed": "a retiré l'absence du rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.confirmed": "a confirmé le rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.services_changed": "a modifié les services du rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.updated": "a modifié le rendez-vous {deLabel}",
    "ActivityLog.sentence.appointment.deleted": "a supprimé le rendez-vous {deLabel}",
    "ActivityLog.sentence.blocked_slot.created": "a bloqué une plage ({label})",
    "ActivityLog.sentence.blocked_slot.updated": "a modifié une plage bloquée ({label})",
    "ActivityLog.sentence.blocked_slot.deleted": "a supprimé une plage bloquée ({label})",

    "ActivityLog.sentence.payment.payment_received": "a encaissé {amount} sur la facture {label}",
    "ActivityLog.sentence.payment.tip_received": "a encaissé un pourboire de {amount} sur la facture {label}",
    "ActivityLog.sentence.payment.refunded": "a remboursé {amount} sur la facture {label}",
    "ActivityLog.sentence.payment.chargeback": "a enregistré une contestation de {amount} sur la facture {label}",
    "ActivityLog.sentence.payment.updated": "a modifié un paiement de la facture {label}",
    "ActivityLog.sentence.payment.deleted": "a supprimé un paiement de {amount} de la facture {label}",
    "ActivityLog.sentence.invoice.created": "a créé la facture {label}",
    "ActivityLog.sentence.invoice.voided": "a annulé la facture {label}",
    "ActivityLog.sentence.invoice.discount_changed": "a modifié le rabais de la facture {label}",
    "ActivityLog.sentence.invoice.updated": "a modifié la facture {label}",
    "ActivityLog.sentence.invoice.deleted": "a supprimé la facture {label}",
    "ActivityLog.sentence.credit_note.created": "a émis la note de crédit {label} de {amount}",
    "ActivityLog.sentence.credit_note.updated": "a modifié la note de crédit {label}",
    "ActivityLog.sentence.credit_note.deleted": "a supprimé la note de crédit {label}",
    "ActivityLog.sentence.cash_movement.drawer_opened": "a ouvert la caisse avec {amount}",
    "ActivityLog.sentence.cash_movement.drawer_closed": "a fermé la caisse à {amount}",
    "ActivityLog.sentence.cash_movement.manual_add": "a ajouté {amount} à la caisse",
    "ActivityLog.sentence.cash_movement.manual_withdraw": "a retiré {amount} de la caisse",
    "ActivityLog.sentence.cash_movement.vault_deposit": "a déposé {amount} au coffre",
    "ActivityLog.sentence.cash_movement.tip_payout": "a versé {amount} de pourboires",
    "ActivityLog.sentence.cash_movement.deleted": "a supprimé un mouvement de caisse de {amount}",
    "ActivityLog.sentence.gift_card.created": "a créé la carte-cadeau {label} de {amount}",
    "ActivityLog.sentence.gift_card.updated": "a modifié la carte-cadeau {label}",
    "ActivityLog.sentence.gift_card.deleted": "a supprimé la carte-cadeau {label}",
    "ActivityLog.sentence.gift_card.balance_adjusted": "a ajusté de {amount} le solde de la carte-cadeau {label}",
    "ActivityLog.sentence.balance.added": "a ajouté {amount} au solde {deLabel}",
    "ActivityLog.sentence.balance.cancelled": "a annulé {amount} du solde {deLabel}",

    "ActivityLog.sentence.customer.created": "a créé la fiche {deLabel}",
    "ActivityLog.sentence.customer.updated": "a modifié la fiche {deLabel}",
    "ActivityLog.sentence.customer.contact_changed": "a modifié les coordonnées {deLabel}",
    "ActivityLog.sentence.customer.blacklisted": "a ajouté {label} à la liste noire",
    "ActivityLog.sentence.customer.unblacklisted": "a retiré {label} de la liste noire",
    "ActivityLog.sentence.customer.merged": "a fusionné la fiche {deLabel}",
    "ActivityLog.sentence.customer.deleted": "a supprimé la fiche {deLabel}",
    "ActivityLog.sentence.technical_sheet.created": "a ajouté une note technique à la fiche {deLabel}",
    "ActivityLog.sentence.technical_sheet.updated": "a modifié une note technique {deLabel}",
    "ActivityLog.sentence.technical_sheet.deleted": "a supprimé une note technique {deLabel}",
    "ActivityLog.sentence.import.imported": "a importé {count} {kind}",

    "ActivityLog.field.unknown": "Champ modifié",
    "ActivityLog.field.appointment_date": "Date",
    "ActivityLog.field.appointment_time": "Heure",
    "ActivityLog.field.hairdresser_name": "Employé",
    "ActivityLog.field.hairdresser_id": "Employé",
    "ActivityLog.field.status": "Statut",
    "ActivityLog.field.attendance_status": "Présence",
    "ActivityLog.field.service_name": "Services",
    "ActivityLog.field.total_price": "Prix total",
    "ActivityLog.field.total_duration": "Durée totale (min)",
    "ActivityLog.field.notes": "Notes",
    "ActivityLog.field.staff_note": "Note du personnel",
    "ActivityLog.field.blocked_date": "Date",
    "ActivityLog.field.start_time": "Début",
    "ActivityLog.field.end_time": "Fin",
    "ActivityLog.field.reason": "Motif",
    "ActivityLog.field.is_full_day": "Journée complète",
    "ActivityLog.field.room_id": "Salle",
    "ActivityLog.field.room_name": "Salle",
    "ActivityLog.field.occurrences": "Plages créées",
    "ActivityLog.field.employees": "Employés",
    "ActivityLog.field.amount": "Montant",
    "ActivityLog.field.payment_method": "Mode de paiement",
    "ActivityLog.field.payment_type": "Type de paiement",
    "ActivityLog.field.discount": "Rabais",
    "ActivityLog.field.is_complimentary": "Gracieuseté",
    "ActivityLog.field.tax_exempt": "Exempt de taxes",
    "ActivityLog.field.settled_externally": "Réglée hors Flowcut",
    "ActivityLog.field.deleted_at": "Supprimé le",
    "ActivityLog.field.applied_to_invoice_id": "Appliquée à une facture",
    "ActivityLog.field.initial_amount": "Montant initial",
    "ActivityLog.field.expiration_date": "Date d'expiration",
    "ActivityLog.field.primary_customer_id": "Titulaire",
    "ActivityLog.field.allergies": "Allergies",
    "ActivityLog.field.style_notes": "Notes de style",
    "ActivityLog.field.internal_notes": "Notes internes",
    "ActivityLog.field.preferred_stylist_id": "Employé(e) préféré(e)",
    "ActivityLog.field.preferred_stylist_name": "Employé(e) préféré(e)",
    "ActivityLog.field.preferred_products": "Produits préférés",
    "ActivityLog.field.client_type": "Type de client",
    "ActivityLog.field.is_blacklisted": "Liste noire",
    "ActivityLog.field.blacklist_reason": "Motif de la liste noire",
    "ActivityLog.field.birth_date": "Date de naissance",
    "ActivityLog.field.birth_day": "Jour de naissance",
    "ActivityLog.field.birth_month": "Mois de naissance",
    "ActivityLog.field.gender": "Genre",
    "ActivityLog.field.secondary_phone": "Téléphone secondaire",
    "ActivityLog.field.emergency_contact_name": "Contact d'urgence",
    "ActivityLog.field.emergency_contact_phone": "Téléphone du contact d'urgence",
    "ActivityLog.field.emergency_contact_relation": "Lien avec le contact d'urgence",
    "ActivityLog.field.acquisition_source": "Comment il a connu le salon",
    "ActivityLog.field.consent_collected": "Consentement recueilli",
    "ActivityLog.field.full_name": "Nom",
    "ActivityLog.field.email": "Courriel",
    "ActivityLog.field.phone": "Téléphone",
    "ActivityLog.field.merged_into_id": "Fusionnée dans une autre fiche",
    "ActivityLog.field.note": "Note",
    "ActivityLog.field.entry_date": "Date de la note",
    "ActivityLog.field.employee_name": "Auteur de la note",
    "ActivityLog.field.count": "Nombre",
    "ActivityLog.field.kind": "Type",

    "ActivityLog.value.empty": "Aucun",
    "ActivityLog.value.emptyFeminine": "Aucune",
    "ActivityLog.value.set": "Défini",
    "ActivityLog.value.setFeminine": "Définie",
    "ActivityLog.value.yes": "Oui",
    "ActivityLog.value.no": "Non",
    "ActivityLog.value.status.completed": "Terminé",
    "ActivityLog.value.status.partially_paid": "Partiellement payée",
    "ActivityLog.value.status.approved": "Approuvée",
    "ActivityLog.value.status.applied": "Appliquée",
    "ActivityLog.value.status.expired": "Expirée",
    "ActivityLog.value.payment_type.payment": "Paiement",
    "ActivityLog.value.payment_type.tip": "Pourboire",
    "ActivityLog.value.payment_type.refund": "Remboursement",
    "ActivityLog.value.payment_type.chargeback": "Contestation",
    "ActivityLog.value.masked": "Masqué",
    "ActivityLog.value.erased": "Effacé",
    "ActivityLog.tab.customer": "Journal",
    "ActivityLog.tab.employee": "Journal",
    "ActivityLog.appointmentButton": "Historique",
    "ActivityLog.dialogTitle.appointment": "Historique du rendez-vous",
    "ActivityLog.employeeNoAccount": "Cet employé n'a pas de compte relié : aucune action ne peut lui être attribuée.",
  },
};
